import { createEffect, createSignal, onCleanup, type Accessor } from "solid-js";

export type AnchoredPopupPlacement =
	| "bottom"
	| "bottom left"
	| "bottom right"
	| "bottom start"
	| "bottom end"
	| "top"
	| "top left"
	| "top right"
	| "top start"
	| "top end"
	| "left"
	| "left top"
	| "left bottom"
	| "start"
	| "start top"
	| "start bottom"
	| "right"
	| "right top"
	| "right bottom"
	| "end"
	| "end top"
	| "end bottom";

export type AnchoredPopupPosition = {
	top: string;
	left: string;
	minWidth: string;
	placement?: string;
};

const GAP = 8;

const resolvePlacementAxis = (value: string, isRTL: boolean) => {
	if (value === "start") {
		return isRTL ? "right" : "left";
	}
	if (value === "end") {
		return isRTL ? "left" : "right";
	}
	return value;
};

const parsePlacement = (value: string) => {
	const [primary, alignment = "center"] = value.split(" ");
	return { primary, alignment };
};

export const computeAnchoredPopupPosition = (
	triggerEl: HTMLElement,
	listboxEl: HTMLElement,
	placementValue: string,
): AnchoredPopupPosition | null => {
	const triggerRect = triggerEl.getBoundingClientRect();
	const listboxRect = listboxEl.getBoundingClientRect();

	if (listboxRect.width === 0 || listboxRect.height === 0) {
		return null;
	}

	const viewportWidth = window.innerWidth;
	const viewportHeight = window.innerHeight;
	const minWidth = Math.max(triggerRect.width, 200);

	const { primary, alignment } = parsePlacement(placementValue);
	const isRTL = false;
	let resolvedPrimary = resolvePlacementAxis(primary, isRTL);
	const resolvedAlignment = resolvePlacementAxis(alignment, isRTL);

	let calculatedTop = 0;
	let calculatedLeft = 0;

	switch (resolvedPrimary) {
		case "top":
			calculatedTop = triggerRect.top - listboxRect.height - GAP;
			if (resolvedAlignment === "left") {
				calculatedLeft = triggerRect.left;
			} else if (resolvedAlignment === "right") {
				calculatedLeft =
					triggerRect.left + triggerRect.width - listboxRect.width;
			} else {
				calculatedLeft =
					triggerRect.left + triggerRect.width / 2 - listboxRect.width / 2;
			}
			break;
		case "bottom":
			calculatedTop = triggerRect.bottom + GAP;
			if (resolvedAlignment === "left") {
				calculatedLeft = triggerRect.left;
			} else if (resolvedAlignment === "right") {
				calculatedLeft =
					triggerRect.left + triggerRect.width - listboxRect.width;
			} else {
				calculatedLeft =
					triggerRect.left + triggerRect.width / 2 - listboxRect.width / 2;
			}
			break;
		case "left":
			calculatedLeft = triggerRect.left - listboxRect.width - GAP;
			if (resolvedAlignment === "top") {
				calculatedTop = triggerRect.top;
			} else if (resolvedAlignment === "bottom") {
				calculatedTop = triggerRect.bottom - listboxRect.height;
			} else {
				calculatedTop =
					triggerRect.top + triggerRect.height / 2 - listboxRect.height / 2;
			}
			break;
		case "right":
			calculatedLeft = triggerRect.right + GAP;
			if (resolvedAlignment === "top") {
				calculatedTop = triggerRect.top;
			} else if (resolvedAlignment === "bottom") {
				calculatedTop = triggerRect.bottom - listboxRect.height;
			} else {
				calculatedTop =
					triggerRect.top + triggerRect.height / 2 - listboxRect.height / 2;
			}
			break;
	}

	const listboxRight = calculatedLeft + listboxRect.width;
	const listboxBottom = calculatedTop + listboxRect.height;

	if (resolvedPrimary === "top" && calculatedTop < 0) {
		resolvedPrimary = "bottom";
		calculatedTop = triggerRect.bottom + GAP;
		if (resolvedAlignment === "left") {
			calculatedLeft = triggerRect.left;
		} else if (resolvedAlignment === "right") {
			calculatedLeft =
				triggerRect.left + triggerRect.width - listboxRect.width;
		} else {
			calculatedLeft =
				triggerRect.left + triggerRect.width / 2 - listboxRect.width / 2;
		}
	} else if (resolvedPrimary === "bottom" && listboxBottom > viewportHeight) {
		resolvedPrimary = "top";
		calculatedTop = triggerRect.top - listboxRect.height - GAP;
		if (resolvedAlignment === "left") {
			calculatedLeft = triggerRect.left;
		} else if (resolvedAlignment === "right") {
			calculatedLeft =
				triggerRect.left + triggerRect.width - listboxRect.width;
		} else {
			calculatedLeft =
				triggerRect.left + triggerRect.width / 2 - listboxRect.width / 2;
		}
	}

	if (resolvedPrimary === "left" && calculatedLeft < 0) {
		resolvedPrimary = "right";
		calculatedLeft = triggerRect.right + GAP;
		if (resolvedAlignment === "top") {
			calculatedTop = triggerRect.top;
		} else if (resolvedAlignment === "bottom") {
			calculatedTop = triggerRect.bottom - listboxRect.height;
		} else {
			calculatedTop =
				triggerRect.top + triggerRect.height / 2 - listboxRect.height / 2;
		}
	} else if (resolvedPrimary === "right" && listboxRight > viewportWidth) {
		resolvedPrimary = "left";
		calculatedLeft = triggerRect.left - listboxRect.width - GAP;
		if (resolvedAlignment === "top") {
			calculatedTop = triggerRect.top;
		} else if (resolvedAlignment === "bottom") {
			calculatedTop = triggerRect.bottom - listboxRect.height;
		} else {
			calculatedTop =
				triggerRect.top + triggerRect.height / 2 - listboxRect.height / 2;
		}
	}

	const left = Math.max(
		0,
		Math.min(calculatedLeft, viewportWidth - listboxRect.width),
	);
	const top = Math.max(
		0,
		Math.min(calculatedTop, viewportHeight - listboxRect.height),
	);

	const actualPlacement =
		resolvedAlignment === "center"
			? resolvedPrimary
			: `${resolvedPrimary} ${resolvedAlignment}`;

	return {
		top: `${top}px`,
		left: `${left}px`,
		minWidth: `${minWidth}px`,
		placement: actualPlacement,
	};
};

export const useAnchoredPopupPosition = (params: {
	isOpen: Accessor<boolean>;
	placement: Accessor<AnchoredPopupPlacement | undefined>;
	getTriggerEl: () => HTMLElement | undefined;
	getListboxEl: () => HTMLElement | undefined;
}) => {
	const [position, setPosition] = createSignal<AnchoredPopupPosition | null>(
		null,
	);

	const update = () => {
		const triggerEl = params.getTriggerEl();
		const listboxEl = params.getListboxEl();
		if (!triggerEl || !listboxEl) {
			return;
		}
		const next = computeAnchoredPopupPosition(
			triggerEl,
			listboxEl,
			params.placement() ?? "bottom",
		);
		if (next) {
			setPosition(next);
		} else {
			setTimeout(update, 0);
		}
	};

	createEffect(() => {
		if (!params.isOpen()) {
			setPosition(null);
			return;
		}
		params.placement();
		requestAnimationFrame(() => {
			requestAnimationFrame(update);
		});
	});

	createEffect(() => {
		if (!params.isOpen()) {
			return;
		}
		window.addEventListener("resize", update);
		window.addEventListener("scroll", update, true);
		onCleanup(() => {
			window.removeEventListener("resize", update);
			window.removeEventListener("scroll", update, true);
		});
	});

	return { position, updatePosition: update };
};
